import { useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";

// Render a torus directly from its parametric equations, without a 3D library.
export default function MathStudy() {
  const canvasRef = useRef(null);
  const [playing, setPlaying] = useState(
    () => !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [mode, setMode] = useState(0);
  const angleRef = useRef(0.55);
  const visibleRef = useRef(true);
  useEffect(() => {
    const canvas = canvasRef.current,
      context = canvas.getContext("2d");
    if (!context) return undefined;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const reduceMotion = () => {
      if (motion.matches) setPlaying(false);
    };
    motion.addEventListener("change", reduceMotion);
    let frame,
      last = 0;
    const draw = () => {
      const size = canvas.clientWidth,
        ratio = Math.min(window.devicePixelRatio || 1, 2);
      if (canvas.width !== Math.round(size * ratio)) {
        canvas.width = Math.round(size * ratio);
        canvas.height = Math.round(size * ratio);
      }
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, size, size);
      const angle = angleRef.current;
      const project = (u, v) => {
        const r = mode === 0 ? 0.58 : 0.37 + 0.22 * Math.sin(u * 3);
        const x = (1.3 + r * Math.cos(v)) * Math.cos(u),
          y = (1.3 + r * Math.cos(v)) * Math.sin(u),
          z = r * Math.sin(v);
        const x1 = x * Math.cos(angle) - y * Math.sin(angle),
          y1 = x * Math.sin(angle) + y * Math.cos(angle);
        const y2 = y1 * Math.cos(0.93) - z * Math.sin(0.93),
          z2 = y1 * Math.sin(0.93) + z * Math.cos(0.93);
        const scale = size * 0.207 * (5.5 / (5.5 - z2));
        return [size / 2 + x1 * scale, size / 2 + y2 * scale, z2];
      };
      const line = (points) => {
        const depth = points.reduce((sum, p) => sum + p[2], 0) / points.length;
        context.beginPath();
        context.strokeStyle = `rgba(170, 61, 31, ${0.24 + (depth + 2) * 0.13})`;
        context.lineWidth = size > 400 ? 0.85 : 0.7;
        points.forEach((p, i) =>
          i ? context.lineTo(p[0], p[1]) : context.moveTo(p[0], p[1]),
        );
        context.stroke();
      };
      for (let i = 0; i < 48; i++)
        line(
          Array.from({ length: 57 }, (_, j) =>
            project((i / 48) * Math.PI * 2, (j / 56) * Math.PI * 2),
          ),
        );
      for (let j = 0; j < 24; j++)
        line(
          Array.from({ length: 97 }, (_, i) =>
            project((i / 96) * Math.PI * 2, (j / 24) * Math.PI * 2),
          ),
        );
    };
    const animate = (time) => {
      if (time - last > 40 && visibleRef.current && !document.hidden) {
        if (playing) angleRef.current += 0.004;
        draw();
        last = time;
      }
      if (playing) frame = window.requestAnimationFrame(animate);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
    });
    observer.observe(canvas);
    const resize = new ResizeObserver(draw);
    resize.observe(canvas);
    draw();
    if (playing) frame = window.requestAnimationFrame(animate);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      resize.disconnect();
      motion.removeEventListener("change", reduceMotion);
    };
  }, [playing, mode]);
  return (
    <div className="math-study">
      <div className="study-topline mono">
        <span>AN EXERCISE IN SEEING</span>
        <span>R² → R³</span>
      </div>
      <div className="study-canvas-wrap">
        <div className="study-orbit" aria-hidden="true" />
        <span className="axis-label axis-y mono" aria-hidden="true">
          y
        </span>
        <span className="axis-label axis-x mono" aria-hidden="true">
          x
        </span>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={
            mode === 0
              ? "A slowly rotating wireframe torus, drawn from parametric equations"
              : "A wireframe torus with a wave varying its tube radius"
          }
        />
        <span className="study-coordinate mono" aria-hidden="true">
          (u, v)
        </span>
      </div>
      <div className="study-bottomline">
        <div className="study-formula">
          <span className="mono">
            {mode === 0 ? "01 / TORUS STUDY" : "02 / WAVE STUDY"}
          </span>
          <span>
            {mode === 0 ? "x = (R + r cos v) cos u" : "r = 0.37 + 0.22 sin(3u)"}
          </span>
        </div>
        <div className="study-controls">
          <button
            onClick={() => setMode(mode === 0 ? 1 : 0)}
            aria-label="Change the mathematical form"
            title="Change the form"
          >
            <RotateCcw size={15} />
          </button>
          <button
            onClick={() => setPlaying(!playing)}
            aria-label={
              playing
                ? "Pause mathematical animation"
                : "Play mathematical animation"
            }
            aria-pressed={playing}
            title={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause size={15} /> : <Play size={15} />}
          </button>
        </div>
      </div>
    </div>
  );
}
